import React, { useEffect, useMemo, useRef } from "react";
// @ts-ignore - react-big-calendar types
import { momentLocalizer } from "react-big-calendar";
import moment from "moment-timezone";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { useAuth } from "../../../../../context/AuthContext";
import { useCalendar } from "../../../../../context/CalendarContext";
import { useSalon } from "../../../../../context/SalonContext";
import {
  CalendarEventType,
  SlotInfo as CalendarSlotInfo,
} from "../../../../../types/calendar";
import { BusinessInfo } from "../../../../../types/context";
import CalendarToolbar from "./CalendarToolbar";
import DnDCalendar, {
  CalendarDropInfo,
  CalendarResizeInfo,
} from "./DnDCalendar";
import {
  buildResources,
  buildOverridesMap,
  createSlotPropGetter,
  createHandleSelectSlot,
  calendarFormats,
  isEventDraggable,
  eventTooltipAccessor,
  getSalonNow,
} from "./sharedCalendarUtils";
import {
  GRID_STEP_MINS,
  GRID_TIMESLOTS,
  NowGutterWrapper,
  openingScrollTime,
  SlotHoverWrapper,
  StaffColumnHeader,
  BlockedStaffContext,
} from "./gridParts";
import type { BlockedStaff } from "../../booking/skillBlock";
import { useGridZoom } from "./useGridZoom";
import {
  EventBlock,
  EventBlockContext,
  makeEventPropGetter,
  type EventFlag,
} from "./EventBlock";
import { laneLayout } from "./laneLayout";
import styles from "./Views.module.css";

const localizer = momentLocalizer(moment);

// ── RBC custom components ────────────────────────────────────────────────
// All module scope, and the `components` object below is a module constant:
// RBC remounts a custom component whenever the `components` identity changes,
// which would drop the toolbar's local state (open Filters / date picker
// popover) on every filter toggle or navigation, and can swallow a click that
// lands mid-remount. Changing data is read from context inside each component,
// never closed over.

interface RbcToolbarProps {
  date: Date;
  onNavigate: (action: "PREV" | "NEXT" | "TODAY") => void;
}

const DayToolbar = ({ date, onNavigate }: RbcToolbarProps) => (
  <CalendarToolbar date={date} view="day" onNavigate={onNavigate} />
);

// Day column header ("Mon 3"), today highlighted in the salon's timezone.
const CustomHeader = ({ label, date }: { label: string; date: Date }) => {
  const { businessInfo } = useSalon() as {
    businessInfo: { timezone?: string } | null;
  };
  const timezone = businessInfo?.timezone;
  const [dayName, dayNumber] = label.split(" ");
  const isToday = moment(date).isSame(getSalonNow(timezone), "day");

  return (
    <div className={isToday ? styles.headerToday : ""}>
      {dayName}{" "}
      <span
        className={`${styles.dayNumber} ${isToday ? styles.dayNumberToday : ""}`}
      >
        {dayNumber}
      </span>
    </div>
  );
};

/** Overlaps sit side by side (every lane shown), 4px inset. */
const DAY_LAYOUT = laneLayout({ gap: 4 });

const DAY_COMPONENTS = {
  toolbar: DayToolbar,
  resourceHeader: StaffColumnHeader,
  event: EventBlock,
  header: CustomHeader,
  timeSlotWrapper: SlotHoverWrapper,
  timeGutterWrapper: NowGutterWrapper,
} as any;

interface DayViewProps {
  events: CalendarEventType[];
  onSelectEvent: (event: CalendarEventType) => void;
  /** Open an event's detail modal directly on double-click. */
  onDoubleClickEvent?: (event: CalendarEventType) => void;
  onSelectSlot?: (slotInfo: CalendarSlotInfo) => void;
  onEventDrop?: (info: CalendarDropInfo) => void;
  onEventResize?: (info: CalendarResizeInfo) => void;
  /** Override which events can be dragged (defaults to isEventDraggable). */
  draggableAccessor?: (event: CalendarEventType) => boolean;
  /** Override which events can be resized (defaults to isEventDraggable). */
  resizableAccessor?: (event: CalendarEventType) => boolean;
  /** Master toggle for resize handles (defaults to true). */
  resizable?: boolean;
  /** Flag chips for a block (calendarFlags over the store). */
  flagsForEvent?: (event: CalendarEventType) => EventFlag[];
  /**
   * Keep this salon wall-clock time ("HH:mm") on screen: whenever it changes
   * and is outside the visible part of the time grid, the grid scrolls to it.
   * RBC only applies `scrollToTime` on mount, so this scrolls explicitly. Used
   * by the booking drawer to follow the active draft row.
   */
  scrollTimeOfDay?: string | null;
  /** Booking drawer: columns whose staff don't offer the active service. */
  blockedStaff?: BlockedStaff | null;
  /** RBC drag start (the dragged appointment's blocked staff columns). */
  onDragStart?: (args: { event: CalendarEventType }) => void;
}

const DayView: React.FC<DayViewProps> = ({
  events,
  onSelectEvent,
  onDoubleClickEvent,
  onSelectSlot,
  onEventDrop,
  onEventResize,
  draggableAccessor = isEventDraggable,
  resizableAccessor = isEventDraggable,
  resizable = true,
  flagsForEvent,
  scrollTimeOfDay = null,
  blockedStaff = null,
  onDragStart,
}) => {
  const {
    filteredEmployees,
    selectedDate,
    setSelectedDate,
    hoursOverrides,
    workingToday,
    workingStaffIds,
    zoom,
    setZoom,
  } = useCalendar();
  const { currentUser } = useAuth() as {
    currentUser: { userId: string } | null;
  };
  const { businessInfo } = useSalon() as {
    businessInfo: BusinessInfo | null;
  };
  const timezone = businessInfo?.timezone;

  // Ad-hoc hours overrides for the visible week overlay the weekly
  // working-hours shading for the dates they cover (see createSlotPropGetter).
  // CalendarContext owns the fetch (also feeds "Working today").
  const overridesMap = useMemo(
    () => buildOverridesMap(hoursOverrides),
    [hoursOverrides],
  );

  // Opens at the salon's opening time minus 30 min; zoom keeps the top
  // minute anchored (useGridZoom owns the scroll, not RBC).
  const opening = openingScrollTime(businessInfo, selectedDate);

  // Follow scrollTimeOfDay (see prop doc). The grid spans 00:00–24:00 (no
  // min/max set), so the target's offset is its share of the day.
  // Re-checked on a day change too (RBC may re-apply its own mount scroll).
  const containerRef = useRef<HTMLDivElement | null>(null);
  const zoomStyle = useGridZoom(containerRef, {
    zoom,
    setZoom,
    openingMinutes: opening.getHours() * 60 + opening.getMinutes(),
  });
  const viewedDayKey = selectedDate.toDateString();
  useEffect(() => {
    if (!scrollTimeOfDay) return;
    const content =
      containerRef.current?.querySelector<HTMLElement>(".rbc-time-content");
    if (!content) return;
    const [h, m] = scrollTimeOfDay.split(":").map(Number);
    const minutes = (h || 0) * 60 + (m || 0);
    const top = (content.scrollHeight * minutes) / (24 * 60);
    const margin = 40;
    const visibleTop = content.scrollTop;
    const visibleBottom = visibleTop + content.clientHeight;
    if (top < visibleTop + margin || top > visibleBottom - margin) {
      content.scrollTop = Math.max(0, top - margin);
    }
  }, [scrollTimeOfDay, viewedDayKey]);

  // "Working today": drop the columns of staff not working on the viewed date
  // (matching the Filters list). Working set is computed in CalendarContext
  // (weekly schedule + ad-hoc overrides).
  const columnEmployees = useMemo(
    () =>
      workingToday
        ? filteredEmployees.filter((emp) => workingStaffIds.has(emp.id))
        : filteredEmployees,
    [workingToday, filteredEmployees, workingStaffIds],
  );

  // Build resources from employees using shared utility
  const resources = buildResources(columnEmployees, currentUser?.userId);

  // Create slot prop getter and slot selection handler using shared utilities.
  // The overrides map overlays ad-hoc hours onto the weekly shading per date.
  const baseSlotPropGetter = createSlotPropGetter(resources, overridesMap);
  const slotPropGetter = (date: Date, resourceId?: string | number) => {
    const props = baseSlotPropGetter(date, resourceId);
    return resourceId !== undefined &&
      blockedStaff?.staffIds.has(String(resourceId))
      ? {
          ...props,
          className: `${props.className ?? ""} ${styles.skillBlocked}`,
        }
      : props;
  };
  const handleSelectSlot = createHandleSelectSlot(onSelectSlot);
  const eventPropGetter = useMemo(
    () => makeEventPropGetter(timezone, "day"),
    [timezone],
  );
  const blockContext = useMemo(
    () => ({ mode: "day" as const, timezone, flagsFor: flagsForEvent }),
    [timezone, flagsForEvent],
  );

  return (
    <div
      className={styles.calendarContainer}
      ref={containerRef}
      style={zoomStyle}
    >
      <BlockedStaffContext.Provider value={blockedStaff}>
        <EventBlockContext.Provider value={blockContext}>
          <DnDCalendar
            localizer={localizer}
            events={events as any}
            startAccessor="start"
            endAccessor="end"
            tooltipAccessor={eventTooltipAccessor as any}
            defaultView="day"
            date={selectedDate}
            step={GRID_STEP_MINS}
            timeslots={GRID_TIMESLOTS}
            selectable
            enableAutoScroll={false}
            getNow={() => getSalonNow(timezone)}
            onSelectEvent={onSelectEvent as any}
            onDoubleClickEvent={onDoubleClickEvent as any}
            onSelectSlot={handleSelectSlot}
            onNavigate={setSelectedDate}
            resources={resources}
            resourceIdAccessor="id"
            resourceTitleAccessor="title"
            formats={calendarFormats}
            components={DAY_COMPONENTS}
            views={["day"]}
            dayLayoutAlgorithm={DAY_LAYOUT as any}
            eventPropGetter={eventPropGetter as any}
            slotPropGetter={slotPropGetter as any}
            showMultiDayTimes
            onEventDrop={onEventDrop as any}
            onDragStart={onDragStart as any}
            onEventResize={onEventResize as any}
            draggableAccessor={draggableAccessor as any}
            resizableAccessor={resizableAccessor as any}
            resizable={resizable}
          />
        </EventBlockContext.Provider>
      </BlockedStaffContext.Provider>
    </div>
  );
};

export default DayView;
